import { sendToGHL } from 'services/ghl';
import { getAccountID, getWebhookID } from 'services/wp';
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import AlertError from 'components/AlertError';
import setLocalStorage from 'utils/setLocalStorage';
import validateEmail from 'utils/validateEmail';
import { validatePhone, formatPhoneNumber } from 'utils/validatePhone';
import { withSubmissionTimeout, getSubmissionErrorMessage } from 'utils/submissionTimeout';

const GroupHealthForm = ({ openByModal = false }) => {
    const navigate = useNavigate()

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        selectedState: ''
    });

    const [submitted, setSubmitted] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = (e) => {
        let { name, value } = e.target;
        if (name === 'phoneNumber') {
            value = formatPhoneNumber(value);
        }
        setFormData({
            ...formData,
            [name]: value
        });
    };

    //!TODO HANDLING DISCLAIMER
    const [agreeToTerms, setAgreeToTerms] = useState(false);

    const handleAgreeToTerms = (e) => {
        const { name, type, checked } = e.target;

        if (type === 'checkbox' && name === 'agreeToTerms') {
            setAgreeToTerms(checked);
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitted(true);

        const missing = [];
        if (!formData.fullName?.trim()) missing.push('Full Name');
        if (!formData.email?.trim()) {
            missing.push('Email');
        } else if (!validateEmail(formData.email)) {
            missing.push('Email (Invalid format)');
        }
        if (!formData.phoneNumber?.trim()) {
            missing.push('Phone Number');
        } else if (!validatePhone(formData.phoneNumber)) {
            missing.push('Phone Number (10 digits)');
        }
        if (!formData.selectedState) missing.push('State');
        if (!agreeToTerms) missing.push('Terms & Conditions Agreement');

        if (missing.length > 0) {
            return;
        }

        setIsLoading(true);
        setShowErrorMessage(false);

        const cleanPhone = formData.phoneNumber.replace(/[()\-\s]/g, '')

        const jsonData = {
            fullName: formData.fullName,
            email: formData.email,
            phoneNumber: cleanPhone,
            selectedState: formData.selectedState
        };


        try {
            await withSubmissionTimeout(async () => {
                const accountID = await getAccountID();
                const webhookID = await getWebhookID({ formName: "group_health" });

                const sentToGHL = await sendToGHL(jsonData, accountID, webhookID);

                if (sentToGHL) {
                    localStorage.removeItem('groupHealthForm');
                    setLocalStorage('groupHealthForm', JSON.stringify(jsonData), 1000 * 60 * 60);
                } else {
                    throw new Error(sentToGHL);
                }
            });

            setShowSuccessMessage(true);

            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 2000);

            setFormData({
                fullName: '',
                email: '',
                phoneNumber: '',
                selectedState: ''
            });
            setAgreeToTerms(false);
            setSubmitted(false);

            navigate('/gh-next-step');

        } catch (error) {
            setErrorMessage(getSubmissionErrorMessage(error));
            setShowErrorMessage(true);

            setAgreeToTerms(false)

        } finally {
            setIsLoading(false);
        }
    };

    const fullNameInvalid = submitted && !formData.fullName?.trim();
    const emailEmpty = submitted && !formData.email?.trim();
    const emailFormatInvalid = submitted && Boolean(formData.email?.trim()) && !validateEmail(formData.email);
    const emailInvalid = emailEmpty || emailFormatInvalid;
    const phoneEmpty = submitted && !formData.phoneNumber?.trim();
    const phoneFormatInvalid = submitted && Boolean(formData.phoneNumber?.trim()) && !validatePhone(formData.phoneNumber);
    const phoneInvalid = phoneEmpty || phoneFormatInvalid;
    const stateInvalid = submitted && !formData.selectedState;
    const termsInvalid = submitted && !agreeToTerms;

    return (
        <form onSubmit={handleSubmit} noValidate>

            {isLoading && (
                <div className="loader"></div>
            )}

            {showSuccessMessage && (
                <div className="toast toast-center toast-middle z-[9999]">
                    <div className="alert alert-success text-base-100">
                        <span>Message sent successfully.</span>
                    </div>
                </div>
            )}

            <AlertError showError={showErrorMessage} onClose={() => setShowErrorMessage(false)}>
                {errorMessage || 'Something went wrong. Please try again later.'}
            </AlertError>

            <label className="form-control label relative" data-invalid={fullNameInvalid ? "true" : undefined}>
                {fullNameInvalid && (
                    <div className="absolute bg-red-600 -top-1 right-4 text-white text-[9.5px] font-bold px-1.5 py-0.5 shadow rounded z-10 pointer-events-none">
                        Required
                    </div>
                )}
                <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name *"
                    className={`input input-bordered input-sm text-primary w-full ${fullNameInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                    value={formData.fullName}
                    onChange={handleInputChange}
                />
            </label>

            <div className='grid md:grid-cols-2'>
                <label className="form-control label relative" data-invalid={emailInvalid ? "true" : undefined}>
                    {emailInvalid && (
                        <div className="absolute bg-red-600 -top-1 right-4 text-white text-[9.5px] font-bold px-1.5 py-0.5 shadow rounded z-10 pointer-events-none">
                            {emailEmpty ? 'Required' : 'Invalid Email'}
                        </div>
                    )}
                    <input
                        type="email"
                        name="email"
                        placeholder="Email *"
                        className={`input input-bordered input-sm text-primary w-full ${emailInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                        value={formData.email}
                        onChange={handleInputChange}
                    />
                </label>

                <label className="form-control label relative" data-invalid={phoneInvalid ? "true" : undefined}>
                    {phoneInvalid && (
                        <div className="absolute bg-red-600 -top-1 right-4 text-white text-[9.5px] font-bold px-1.5 py-0.5 shadow rounded z-10 pointer-events-none">
                            {phoneEmpty ? 'Required' : 'Please enter a valid 10-digit phone number'}
                        </div>
                    )}
                    <input
                        type="tel"
                        placeholder="(XXX) XXX-XXXX *"
                        name="phoneNumber"
                        className={`input input-bordered input-sm text-primary w-full ${phoneInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                    />
                </label>
            </div>


            <label className="form-control label text-primary relative" data-invalid={stateInvalid ? "true" : undefined}>
                {stateInvalid && (
                    <div className="absolute bg-red-600 -top-1 right-4 text-white text-[9.5px] font-bold px-1.5 py-0.5 shadow rounded z-10 pointer-events-none">
                        Required
                    </div>
                )}
                <select
                    className={`select select-bordered select-sm w-full ${stateInvalid ? 'select-error border-2 border-red-600 bg-red-50' : ''}`}
                    name="selectedState"
                    value={formData.selectedState}
                    onChange={handleInputChange}
                >
                    <option disabled value="">Select State *</option>
                    <option value="Alabama">Alabama</option><option value="Alaska">Alaska</option><option value="Arizona">Arizona</option><option value="Arkansas">Arkansas</option><option value="California">California</option><option value="Colorado">Colorado</option><option value="Connecticut">Connecticut</option><option value="Delaware">Delaware</option><option value="Florida">Florida</option><option value="Georgia">Georgia</option><option value="Hawaii">Hawaii</option><option value="Idaho">Idaho</option><option value="Illinois">Illinois</option><option value="Indiana">Indiana</option><option value="Iowa">Iowa</option><option value="Kansas">Kansas</option><option value="Kentucky">Kentucky</option><option value="Louisiana">Louisiana</option><option value="Maine">Maine</option><option value="Massachusetts">Massachusetts</option><option value="Michigan">Michigan</option><option value="Minnesota">Minnesota</option><option value="Mississippi">Mississippi</option><option value="Missouri">Missouri</option><option value="Montana">Montana</option><option value="Nebraska">Nebraska</option><option value="Nevada">Nevada</option><option value="New Hampshire">New Hampshire</option><option value="New Jersey">New Jersey</option><option value="New Mexico">New Mexico</option><option value="New York">New York</option><option value="North Carolina">North Carolina</option><option value="North Dakota">North Dakota</option><option value="Ohio">Ohio</option><option value="Oklahoma">Oklahoma</option><option value="Oregon">Oregon</option><option value="Pennsylvania">Pennsylvania</option><option value="Rhode Island">Rhode Island</option><option value="South Carolina">South Carolina</option><option value="South Dakota">South Dakota</option><option value="Tennessee">Tennessee</option><option value="Texas">Texas</option><option value="Utah">Utah</option><option value="Vermont">Vermont</option><option value="Virginia">Virginia</option><option value="Washington">Washington</option><option value="West Virginia">West Virginia</option><option value="Wisconsin">Wisconsin</option><option value="Wyoming">Wyoming</option>
                </select>
            </label>



            {/* DISCLAIMER */}
            <label
                className={`label cursor-pointer flex items-start rounded-lg p-2 ${termsInvalid ? 'border-2 border-red-600 bg-red-50' : ''}`}
                data-invalid={termsInvalid ? "true" : undefined}
            >
                <input
                    type="checkbox"
                    className={`checkbox checkbox-secondary rounded mr-4 mt-1 ${termsInvalid ? 'checkbox-error border-red-600' : ''}`}
                    name="agreeToTerms"
                    checked={agreeToTerms}
                    onChange={handleAgreeToTerms}
                />
                <span className={`label-text text-xs ${termsInvalid ? 'text-red-900' : openByModal ? "text-black" : "text-[#f1f1f1]"}`}>
                    {termsInvalid && (
                        <span className="inline-block text-[10px] font-bold text-white bg-red-600 px-1.5 py-0.5 rounded mr-2 mb-1">
                            Required: Please check to agree
                        </span>
                    )}
                    By providing your email address and phone number, you agree to receive email and text messages from
                    Insurance Enterprise USA about our products and services.
                    Email or text HELP to
                    <span className='underline text-blue-600 hover:text-blue-400 px-1'>
                        <a href='mailto:benefits@insuranceenterpriseusa.com' rel='noreferrer'>
                            benefits@insuranceenterpriseusa.com
                        </a>
                    </span>

                    or
                    <a href='tel:8883506605' rel='noreferrer' className='underline text-blue-600 hover:text-blue-400 px-1'>
                        888-350-6605
                    </a>
                    for assistance. You can unsubscribe at any time
                </span>
            </label>

            <label className="form-control label max-w-lg">
                <button
                    className={`btn btn-secondary w-full text-base-100 mt-4 ${isLoading ? 'btn-disabled opacity-50' : ''}`}
                    type="submit"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <span className="loading loading-spinner loading-lg text-white"></span>
                    ) : (
                        'Next Step'
                    )}
                </button>
            </label>


            {/* T&C and Privacy Policy */}
            <div className='flex justify-center underline text-blue-600 text-xs'>
                <NavLink className='px-2' to='/terms-and-condition' target='_blank'>Terms and Conditions</NavLink>
                <NavLink className='px-2' to='/privacy-policy' target='_blank'>Privacy Policy</NavLink>
            </div>
        </form >
    );
}

export default GroupHealthForm;
